<template>
    <div ref="loader">

        <!-- Service group name and refresh button -->
        <div class="w-100 bg-no-status-gray shadow-lg-no-status-gray text-white">
            <b-container class="container-lg">
                <b-row class="p-0 pt-2">
                    <b-col class="col-9 text-start min-width-0">
                        <h5 class="text-truncate">
                            <i class="fa-solid fa-chevron-left" @click="goBack"></i>
                            {{ container.name }}
                        </h5>
                    </b-col>
                    <b-col class="col-3 text-end">
                        <button type="button" class="btn btn-sm btn-secondary" @click="refreshData">
                            <i class="fa-solid fa-arrows-rotate"
                               :class="{'fa-spin': loading}"></i>
                        </button>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col class="col-12">
                        {{ servicegroup.description }}
                    </b-col>
                </b-row>

                <!-- Service status filter buttons -->
                <b-row class="p-0 pt-2 pb-2">
                    <b-col>
                        <i class="fa-solid fa-filter"></i>
                        {{ $t('Service states filter') }}
                    </b-col>
                    <b-col class="col-12 pt-2">
                        <b-button-group class="w-100">
                            <b-button variant="success" @click="toServices('ok')">
                                {{ statusSummary.ok ?? 0 }}
                            </b-button>
                            <b-button variant="warning" @click="toServices('warning')">
                                {{ statusSummary.warning ?? 0 }}
                            </b-button>
                            <b-button variant="danger" @click="toServices('critical')">
                                {{ statusSummary.critical ?? 0 }}
                            </b-button>
                            <b-button variant="secondary" @click="toServices('unknown')">
                                {{ statusSummary.unknown ?? 0 }}
                            </b-button>
                        </b-button-group>
                    </b-col>
                </b-row>
            </b-container>
        </div>
        <!-- End of service group header -->

        <!-- List all services inside of this service group -->
        <b-container class="container-lg">
            <b-row class="p-0 pt-2">
                <b-col class="text-start">
                    <h5><i class="fa-solid fa-cog"></i> {{ $t('Services') }}</h5>
                </b-col>
            </b-row>

            <!-- Start actual list -->
            <b-list-group
                v-for="service in services"
                v-bind:key="service.Service.id"
                class="mt-2">

                <div class="flex-column align-items-start">
                    <div class="list-group-item flex-column align-items-start list-group-item-action ps-0">

                        <div class="d-flex flex-row min-h-110">
                            <div class="p-2">
                                <Servicestatus v-bind:current_state="service.Servicestatus.currentState"
                                               v-bind:human_state="service.Servicestatus.humanState"></Servicestatus>
                            </div>
                            <div class="min-width-0">
                                <div class="row p-2">
                                    <div class="col-12 text-truncate h5">
                                        <router-link :to="{name: 'servicesDetails', params: {id: service.Service.id}}">
                                            {{ service.Service.servicename }}
                                        </router-link>
                                    </div>
                                    <div class="col-12 text-truncate">
                                        {{ service.Service.description }}
                                    </div>
                                    <div class="col-12 text-truncate">
                                        <div class="row">
                                            <div class="col-12 text-truncate">
                                                <i class="fa-solid fa-desktop"></i>
                                                {{ service.Host.hostname }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="row p-2">
                                    <div class="col small">
                                        {{ $t('Host') }}:
                                        <span :class="['badge', 'text-white', service.Hoststatus.cssClass]">
                                        {{ service.Hoststatus.humanState }}
                                        </span>
                                        <span>
                                            <span v-if="service.Hoststatus.currentState > 0 && service.Hoststatus.problemHasBeenAcknowledged" :class="[ 'badge', 'bg-dark', 'text-white']">
                                            <i class="far fa-user"></i> {{ $t('acknowledged') }}
                                            </span>
                                            <span v-if="service.Hoststatus.currentState > 0 && !service.Hoststatus.problemHasBeenAcknowledged" :class="['badge', 'text-white', 'bg-dark']">
                                            <i class="far fa-user"></i> {{ $t('not acknowledged') }}
                                            </span>
                                            <span v-if="service.Hoststatus.scheduledDowntimeDepth > 0" :class="['badge', 'text-white', 'bg-dark']">
                                            <i class="fa fa-power-off"></i> {{ $t('in downtime') }}
                                            </span>
                                        </span>
                                    </div>
                                </div>
                                <div class="row p-2">
                                    <div class="col-12">
                                        <div v-if="service.Servicestatus.currentState > 0 && service.Servicestatus.problemHasBeenAcknowledged">
                                            <i class="far fa-user"></i> {{ $t('Service is acknowledged') }}
                                        </div>
                                        <div v-if="service.Servicestatus.currentState > 0 && !service.Servicestatus.problemHasBeenAcknowledged">
                                            <i class="far fa-user"></i> {{ $t('Service is not acknowledged') }}
                                        </div>
                                    </div>
                                    <div class="col-12">
                                        <div v-if="service.Servicestatus.scheduledDowntimeDepth > 0">
                                            <i class="fa fa-power-off"></i> {{ $t('Service is in downtime') }}
                                        </div>
                                    </div>
                                </div>

                                <div class="row">
                                    <div class="col-12 text-end">
                                        <date-switch v-bind:css_class="service.Servicestatus.humanState"
                                                     v-bind:status="service.Servicestatus"></date-switch>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </b-list-group>
            <!-- end new list -->
            <!-- end of actual list -->

            <!-- Paginator -->
            <b-list-group class="mt-3" ref="noMass">
                <div v-if="services.length > 0">
                    <div class="d-flex w-100 justify-content-between">
                        <small>{{ $t('page') }} {{ paging.page }}</small>
                        <small>{{ $t('pagesize') }} {{ paging.limit }}</small>
                    </div>
                    <div class="d-flex w-100 justify-content-center mt-4">
                        <vue-awesome-paginate
                            :total-items="paging.count"
                            :items-per-page="25"
                            :max-pages-shown="5"
                            v-model="paging.page"
                            @click="getData"
                        />
                    </div>
                </div>
                <b-list-group-item v-if="services.length === 0">
                    <h5 class="mb-1">{{ $t('noitems') }}</h5>
                </b-list-group-item>
            </b-list-group>
            <!-- End of Paginator -->


        </b-container>
        <!-- End of list -->
    </div>

</template>
<script>
import Hoststatus from "../utils/Hoststatus.vue";
import Servicestatus from "../utils/Servicestatus.vue";
import DateSwitch from "../utils/DateSwitch.vue";
import {useToast} from "vue-toastification";
import {VueAwesomePaginate} from "vue-awesome-paginate";
import { BButton } from 'bootstrap-vue-next';

export default {
    name: "ServicegroupsDetails",
    components: {BButton, VueAwesomePaginate, DateSwitch, Servicestatus, Hoststatus},
    props: ['id', 'state'],
    data(){
        return {
            init: false,
            loading: true,
            requestStatus: false,
            all: {},
            servicegroup: {},
            container: {},
            statusSummary: {},
            services: [],
            cumulatedState: -1,
            servicesFilter: {
                filter: {
                    current_state: [],
                }
            },
            paging: {
                page: 1,
                pageCount: 1,
            },
        }
    },
    async mounted(){
        await this.getData();
    },
    methods: {
        getData: function(page = 1){
            let loader = this.$loading.show();
            this.loading = true;
            axios.post('/servicegroups/details.json', {
             /*   id: this.id,
                filter: this.servicesFilter.filter,
                page: page, */
            }, {
                params: {
                    id: this.id,
                    filter: this.servicesFilter,
                    page: page,
                }
            })
                .then(response => {
                    this.requestStatus = true;
                    this.all = response.data.servicegroupDetails.servicegroup;
                    this.servicegroup = response.data.servicegroupDetails.servicegroup.Servicegroup ?? {};
                    this.container = response.data.servicegroupDetails.servicegroup.Servicegroup.container ?? {};
                    if(this.init === false){
                        this.statusSummary = response.data.servicegroupDetails.servicegroup.StatusSummary ?? {};
                    }
                    this.services = response.data.servicegroupDetails.servicegroup.Services ?? [];
                    this.paging = response.data.servicegroupDetails.paging ?? {
                        page: 1,
                        pageCount: 1,
                    };
                    this.init = true;
                })
                .catch(error => {
                    console.log(error);
                    useToast().error(this.$t('error'), {
                        timeout: 2000
                    });
                })
                .finally(() => {
                    loader.hide();
                    this.loading = false;
                });
        },
        refreshData(){
            this.servicesFilter.filter.current_state = [];
            this.getData();
        },
        goBack: function(){
            this.$router.back();
        },
        toServices: function(servicestate){
            this.servicesFilter.filter.current_state = [servicestate];
            this.getData();
        },
    }
}
</script>


<style scoped>

.flex-right {
    margin-left: auto;
}

</style>
